import { useEffect, useState } from 'react';
import { AnalyticsConsent } from '../lib/analytics';
import { OPEN_COOKIE_SETTINGS } from '../lib/consentEvents';
import { useScrollLock } from '../lib/smoothScroll';

interface ConsentBannerProps {
  consent: AnalyticsConsent;
  onConsentChange: (consent: AnalyticsConsent) => void;
  /** Hold the wall back (e.g. while the loader plays) so the first impression is seen. */
  deferred?: boolean;
}

const ConsentBanner = ({ consent, onConsentChange, deferred = false }: ConsentBannerProps) => {
  const [showPreferences, setShowPreferences] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(consent === 'granted');

  useEffect(() => {
    setAnalyticsEnabled(consent === 'granted');
  }, [consent]);

  const blocking = (consent === 'unknown' && !deferred) || showPreferences;

  // Wheel scrolling (Lenis) stays out of the page behind the wall or the preferences
  useScrollLock(blocking);

  // The footer can reopen the preferences at any time, whatever was chosen before
  useEffect(() => {
    const open = () => setShowPreferences(true);
    window.addEventListener(OPEN_COOKIE_SETTINGS, open);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS, open);
  }, []);

  // Block scrolling when the consent wall or preferences modal is open
  useEffect(() => {
    const shouldBlock = blocking;
    document.body.style.overflow = shouldBlock ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [blocking]);

  const acceptAnalytics = () => {
    onConsentChange('granted');
    setShowPreferences(false);
  };

  const rejectAnalytics = () => {
    onConsentChange('denied');
    setShowPreferences(false);
  };

  const savePreferences = () => {
    onConsentChange(analyticsEnabled ? 'granted' : 'denied');
    setShowPreferences(false);
  };

  const shouldShowBanner = consent === 'unknown' && !showPreferences && !deferred;

  return (
    <>
      {shouldShowBanner && (
        <div className="consent-wall" role="dialog" aria-modal="true" aria-label="Cookie voorkeuren">
          <div className="consent-wall__backdrop" />
          <div className="consent-wall__card" data-theme="light">
            <span className="label consent-wall__label">Cookies</span>
            <h2 className="consent-wall__title">We waarderen je privacy</h2>
            <p className="consent-wall__desc">
              We gebruiken cookies en vergelijkbare technologieën om je ervaring te verbeteren, het verkeer te analyseren en advertenties te personaliseren. Door op "Accepteren" te klikken, sta je het gebruik van alle cookies toe.
            </p>
            <div className="consent-wall__actions">
              <button className="consent-button consent-button--primary consent-button--lg" onClick={acceptAnalytics}>
                Alle cookies accepteren
              </button>
              <button className="consent-button consent-button--secondary consent-button--lg" onClick={() => setShowPreferences(true)}>
                Instellingen beheren
              </button>
              <button className="consent-button consent-button--ghost consent-button--sm" onClick={rejectAnalytics}>
                Alleen noodzakelijk
              </button>
            </div>
            <a className="consent-link" href="/privacy-policy.php">
              Lees ons privacybeleid →
            </a>
          </div>
        </div>
      )}

      {showPreferences && (
        <div className="consent-modal" role="dialog" aria-modal="true" aria-label="Cookie instellingen">
          <div className="consent-modal__backdrop" onClick={() => setShowPreferences(false)} />
          <div className="consent-modal__panel" role="document" data-theme="light">
            <div className="consent-modal__header">
              <h3>Cookie instellingen</h3>
              <button
                className="consent-modal__close"
                onClick={() => setShowPreferences(false)}
                aria-label="Sluit cookie instellingen"
              >
                x
              </button>
            </div>
            <div className="consent-modal__body">
              <p>
                Kies welke cookies je wilt toestaan. Analytics en advertentiecookies helpen ons de website te verbeteren en relevante content te tonen.
              </p>

              <div className="consent-toggle-group">
                <label className="consent-toggle">
                  <input type="checkbox" checked disabled />
                  <span className="consent-toggle__slider" />
                  <div className="consent-toggle__info">
                    <span className="consent-toggle__label">Noodzakelijke cookies</span>
                    <span className="consent-toggle__sub">Altijd actief — vereist voor werking</span>
                  </div>
                </label>

                <label className="consent-toggle">
                  <input
                    type="checkbox"
                    checked={analyticsEnabled}
                    onChange={(event) => setAnalyticsEnabled(event.target.checked)}
                  />
                  <span className="consent-toggle__slider" />
                  <div className="consent-toggle__info">
                    <span className="consent-toggle__label">Analytics & advertenties</span>
                    <span className="consent-toggle__sub">Helpt ons het verkeer te analyseren en advertenties te personaliseren</span>
                  </div>
                </label>
              </div>

              <a className="consent-link" href="/privacy-policy.php">
                Lees het privacybeleid →
              </a>
            </div>
            <div className="consent-modal__actions">
              <button className="consent-button consent-button--ghost" onClick={rejectAnalytics}>
                Alles weigeren
              </button>
              <button className="consent-button consent-button--primary" onClick={savePreferences}>
                Voorkeuren opslaan
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
};

export default ConsentBanner;
